import { useCallback, useMemo } from "react";
import { useLocalStorage } from "@adsk/acc-issues-core/utils/hooks/useLocalStorage";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";
import { IssueTemplate } from "@adsk/acc-issues-sdk";
import {
  getFnForGetIssueTemplateById,
  getFnForIsTemplateActive,
  getIsTemplateStatusActive,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTemplates";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getCurrentUserPermittedStatusesForNewIssue } from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";

const RECENTLY_USED_TEMPLATES_COUNT = 3;

export const useRecentlyUsedTemplateIds = ({
  selectedTemplateId,
}: {
  selectedTemplateId?: string;
}) => {
  const [getRecentlyUsedTemplateIds, setRecentlyUsedTemplateIds] =
    useLocalStorage(LocalStorageKeys.LAST_SELECTED_TEMPLATE_IDS);
  const recentlyUsedTemplateIds: string[] = useMemo(() => {
    return getRecentlyUsedTemplateIds() || [];
  }, [getRecentlyUsedTemplateIds]);
  if (
    selectedTemplateId &&
    !recentlyUsedTemplateIds.includes(selectedTemplateId)
  ) {
    recentlyUsedTemplateIds.unshift(selectedTemplateId);
  }
  const onSelectTemplateIdCacheKey = JSON.stringify(recentlyUsedTemplateIds);

  const getTemplateById = useSelector(getFnForGetIssueTemplateById);
  const currentUserPermittedStatusesForNewIssue = useSelector(
    getCurrentUserPermittedStatusesForNewIssue
  );
  const getIsTemplateActive = useSelector(getFnForIsTemplateActive);

  const recentlyUsedTemplatesToDisplay = useMemo(() => {
    return recentlyUsedTemplateIds
      .map((templateId: string) => {
        const template: IssueTemplate | undefined = getTemplateById(templateId);
        if (!template) {
          return null;
        }

        const isTemplateActive = getIsTemplateActive(template);
        if (!isTemplateActive) {
          return null;
        }

        const isTemplateStatusActive = getIsTemplateStatusActive({
          template,
          currentUserPermittedStatusesForNewIssue,
        });
        if (!isTemplateStatusActive) {
          return null;
        }
        return template;
      })
      .filter(x => !!x) as IssueTemplate[];
  }, [
    currentUserPermittedStatusesForNewIssue,
    getIsTemplateActive,
    getTemplateById,
    recentlyUsedTemplateIds,
  ]);

  const addTemplateIdToRecentlyUsed = useCallback(
    (templateId: string) => {
      let newRecentlyUsedTemplateIds = recentlyUsedTemplateIds.filter(
        (recentlyUsedTemplateId: string) =>
          recentlyUsedTemplateId !== templateId
      );
      newRecentlyUsedTemplateIds.unshift(templateId);
      newRecentlyUsedTemplateIds = newRecentlyUsedTemplateIds.slice(
        0,
        RECENTLY_USED_TEMPLATES_COUNT
      );
      setRecentlyUsedTemplateIds(newRecentlyUsedTemplateIds);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelectTemplateIdCacheKey, setRecentlyUsedTemplateIds]
  );
  return {
    recentlyUsedTemplateIds,
    recentlyUsedTemplatesToDisplay,
    addTemplateIdToRecentlyUsed,
  };
};
